import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { Download, Share2, Upload } from 'lucide-react';

import { Button } from '@/components/common/Button/Button';
import { Card } from '@/components/common/Card/Card';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { LoadingState } from '@/components/common/LoadingState/LoadingState';
import { PageBreadcrumb } from '@/components/layout/PageBreadcrumb/PageBreadcrumb';
import { PoliticalMatchShareStory } from '@/components/political-match/PoliticalMatchShareStory/PoliticalMatchShareStoryHtml';
import { buildAnalyticsEventName, trackAnalyticsEvent } from '@/lib/analytics';
import { usePoliticalMatchFlow } from '@/hooks/usePoliticalMatchFlow';
import { usePoliticalMatchUserPhoto } from '@/hooks/usePoliticalMatchUserPhoto';
import { useSeo } from '@/hooks/useSeo';
import { buildPageTitle } from '@/lib/seo';
import { classNames } from '@/utils/classNames';
import {
  calculatePoliticalMatchDistance,
  politicalMatchCloseCandidateDistanceLimit,
  sortPoliticalMatchMatchesByDistance,
} from '@/utils/political-match';

import styles from '@/styles/positions.module.css';

const shareFileName = 'verdeconfirma-meu-match-politico.png';
const shareCandidatePhotoCheckboxId = 'match-politico-share-candidate-photo';
const shareCandidatePhotoHintId = `${shareCandidatePhotoCheckboxId}-hint`;

async function waitForStoryCaptureAssets(node: HTMLElement): Promise<void> {
  const imageElements = Array.from(node.querySelectorAll('img'));

  await Promise.all(
    imageElements.map(async (image) => {
      if (image.complete && image.naturalWidth > 0) {
        return;
      }

      if (typeof image.decode === 'function') {
        try {
          await image.decode();
          return;
        } catch {
          // Fall through to load/error listeners below.
        }
      }

      await new Promise<void>((resolve) => {
        const finish = () => resolve();
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
      });
    }),
  );

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  await new Promise<void>((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function downloadBlob(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = 'noreferrer';
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export function MatchPoliticoSharePage(): JSX.Element {
  const { state, status, error } = usePoliticalMatchFlow();
  const [searchParams] = useSearchParams();
  const storyPreviewRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { userPhotoUrl, setUserPhotoUrl } = usePoliticalMatchUserPhoto();
  const [includeCandidatePhoto, setIncludeCandidatePhoto] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const summary = useMemo(() => {
    if (!state.mode) {
      return null;
    }

    return {
      economicScore: state.economicScore ?? null,
      personalScore: state.personalScore ?? null,
    };
  }, [state.economicScore, state.mode, state.personalScore]);
  const matches = state.matches ?? [];
  const orderedMatches = useMemo(
    () => sortPoliticalMatchMatchesByDistance(matches, summary?.economicScore ?? null, summary?.personalScore ?? null),
    [matches, summary],
  );
  const closestCandidateDistance = useMemo(() => {
    if (!summary || orderedMatches.length === 0) {
      return null;
    }

    const closestMatch = orderedMatches[0];

    if (!closestMatch) {
      return null;
    }

    return calculatePoliticalMatchDistance(
      summary.economicScore,
      summary.personalScore,
      closestMatch.position.economicScore,
      closestMatch.position.personalScore,
    );
  }, [orderedMatches, summary]);
  const hasCloseCandidate = closestCandidateDistance !== null && closestCandidateDistance <= politicalMatchCloseCandidateDistanceLimit;
  const featuredCandidateId = searchParams.get('candidateId')?.trim() || null;
  const featuredCandidateMatch = useMemo(
    () => matches.find((match) => match.candidate.id === featuredCandidateId) ?? null,
    [featuredCandidateId, matches],
  );
  const featuredCandidateMatchForStory = hasCloseCandidate ? featuredCandidateMatch ?? undefined : null;

  const handleIncludeCandidatePhotoChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const nextIncludeCandidatePhoto = event.currentTarget.checked;

    setIncludeCandidatePhoto(nextIncludeCandidatePhoto);
    trackAnalyticsEvent(
      buildAnalyticsEventName(
        nextIncludeCandidatePhoto ? 'Incluir foto candidato' : 'Remover foto candidato',
        `match:share:candidate-photo:${nextIncludeCandidatePhoto ? 'on' : 'off'}`,
        'toggle',
      ),
    );
  };

  useSeo({
    title: buildPageTitle('Compartilhar meu resultado'),
    description: 'Gere uma imagem vertical do seu Match Político para compartilhar no Instagram Stories.',
    canonicalPath: '/match-politico/compartilhar',
    indexable: false,
  });

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setStatusMessage('Escolha uma imagem válida para personalizar seu marcador.');
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setUserPhotoUrl(typeof reader.result === 'string' ? reader.result : null);
      setStatusMessage('Foto adicionada à prévia.');
    };

    reader.onerror = () => {
      setStatusMessage('Não foi possível ler sua foto agora.');
    };

    reader.readAsDataURL(file);
  };

  const generateStoryBlob = async (): Promise<Blob> => {
    const storyPreviewNode = storyPreviewRef.current;

    if (!storyPreviewNode) {
      throw new Error('A prévia ainda não está pronta.');
    }

    await waitForStoryCaptureAssets(storyPreviewNode);

    const { toBlob } = await import('html-to-image');

    const blob = await toBlob(storyPreviewNode, {
      cacheBust: true,
      pixelRatio: 1,
      backgroundColor: '#f4faf6',
      style: {
        position: 'relative',
        left: '0px',
        top: '0px',
        transform: 'none',
        width: '1080px',
        height: '1920px',
        overflow: 'hidden',
      },
    });

    if (!blob) {
      throw new Error('Não foi possível gerar a imagem do Story.');
    }

    return blob;
  };

  const handleDownload = async (): Promise<void> => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    setStatusMessage(null);

    try {
      const blob = await generateStoryBlob();
      downloadBlob(blob, shareFileName);
      setStatusMessage('Imagem salva no seu dispositivo.');
    } catch (downloadError) {
      setStatusMessage(downloadError instanceof Error ? downloadError.message : 'Não foi possível salvar a imagem agora.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleShare = async (): Promise<void> => {
    if (isExporting) {
      return;
    }

    setIsExporting(true);
    setStatusMessage(null);

    try {
      const blob = await generateStoryBlob();
      const file = new File([blob], shareFileName, { type: 'image/png' });
      const sharePayload = {
        title: 'Meu Match Político',
        text: 'Minha posição e os candidatos mais próximos de mim no Match Político do Verdeconfirma.',
        files: [file],
      };
      let canShareFiles = true;

      try {
        canShareFiles = typeof navigator.canShare === 'function' ? navigator.canShare({ files: [file] }) : true;
      } catch {
        canShareFiles = false;
      }

      if (typeof navigator.share === 'function' && canShareFiles) {
        await navigator.share(sharePayload);
        setStatusMessage('Compartilhamento aberto no seu dispositivo.');
        return;
      }

      downloadBlob(blob, shareFileName);
      setStatusMessage('Seu navegador não abriu o compartilhamento direto. A imagem foi salva.');
    } catch (shareError) {
      if (shareError instanceof DOMException && shareError.name === 'AbortError') {
        setStatusMessage(null);
        return;
      }

      setStatusMessage(shareError instanceof Error ? shareError.message : 'Não foi possível compartilhar agora.');
    } finally {
      setIsExporting(false);
    }
  };

  if (status === 'loading') {
    return <LoadingState title="Carregando compartilhamento" messages={['Preparando a prévia', 'Consultando seu resultado', 'Montando o Story']} />;
  }

  if (status === 'error') {
    return <EmptyState tone="danger" title="Não foi possível carregar o resultado" description={error ?? ''} />;
  }

  if (!state.mode) {
    return <Navigate to="/match-politico" replace />;
  }

  if (!summary) {
    return <Navigate to="/match-politico/resumo" replace />;
  }

  return (
    <section className={styles.sharePage}>
      <PageBreadcrumb items={[{ label: 'Match Político', to: '/match-politico' }, { label: 'Resultado', to: '/match-politico/resumo' }, { label: 'Compartilhar' }]} />
      <Button to="/match-politico/resumo" variant="secondary" size="sm">
        Voltar ao resultado
      </Button>

      <div className={styles.shareLayout}>
        <Card tone="soft" className={styles.shareControlsCard}>
          <input ref={fileInputRef} className={styles.shareUploadInput} type="file" accept="image/*" onChange={handlePhotoChange} />

          <div className={styles.shareToggle}>
            <input
              id={shareCandidatePhotoCheckboxId}
              className={styles.shareToggleInput}
              type="checkbox"
              checked={includeCandidatePhoto}
              onChange={handleIncludeCandidatePhotoChange}
              disabled={isExporting}
              aria-describedby={shareCandidatePhotoHintId}
            />
            <div className={styles.shareToggleCopy}>
              <label htmlFor={shareCandidatePhotoCheckboxId} className={styles.shareToggleLabel}>
                Incluir foto do candidato na imagem compartilhável
              </label>
              <p id={shareCandidatePhotoHintId} className={styles.shareToggleHint}>
                Desmarque para gerar a arte sem a foto do candidato selecionado no resumo.
              </p>
            </div>
          </div>

          <div className={styles.shareActions}>
            <Button type="button" variant="secondary" fullWidth disabled={isExporting} onClick={() => fileInputRef.current?.click()}>
              <Upload size={18} />
              {userPhotoUrl ? 'Trocar minha foto' : 'Adicionar minha foto'}
            </Button>

            {userPhotoUrl ? (
              <Button
                type="button"
                variant="ghost"
                fullWidth
                disabled={isExporting}
                onClick={() => {
                  setUserPhotoUrl(null);
                  setStatusMessage('Foto removida da prévia.');
                }}
              >
                Remover foto
              </Button>
            ) : null}

            <Button type="button" variant="primary" fullWidth disabled={isExporting} onClick={() => void handleShare()}>
              <Share2 size={18} />
              Compartilhar pelo dispositivo
            </Button>

            <Button type="button" variant="secondary" fullWidth disabled={isExporting} onClick={() => void handleDownload()}>
              <Download size={18} />
              Salvar imagem
            </Button>
          </div>

          {statusMessage ? (
            <p className={classNames(styles.shareStatus, isExporting && styles.shareStatusBusy)} role="status">
              {statusMessage}
            </p>
          ) : null}
        </Card>
        <Card className={styles.sharePreviewCard}>
          <div className={styles.sharePreviewFrame}>
            <PoliticalMatchShareStory
              ref={storyPreviewRef}
              economicScore={summary.economicScore}
              personalScore={summary.personalScore}
              matches={matches}
              featuredCandidateMatch={featuredCandidateMatchForStory}
              userPhotoUrl={userPhotoUrl}
              includeCandidatePhoto={includeCandidatePhoto && hasCloseCandidate}
            />
          </div>
        </Card>
      </div>
    </section>
  );
}
